/**
 * Client-side sprite post-processing (35j). box-api returns the RAW NanoBanana sheet; the studio keys the
 * background, despeckles, slices and normalises entirely in-canvas (no server image lib, no S3 CORS taint).
 * Produces a clean transparent atlas: one uniform grid where every frame is the same size, foot-aligned.
 *
 * Pipeline (all proven during R&D):
 *  1. key background = the most-frequent (mode) colour, global-keyed within tolerance (handles white/tan/
 *     blue/etc flat backgrounds); interior whites survive because only the mode colour is removed.
 *  2. per cell keep the largest connected alpha component (drops stray shadow nubs / grid-line bits).
 *  3. auto-crop each cell to its alpha bbox (the model grid is never pixel-perfect).
 *  4. scale every crop to one common character height, foot-align on a shared baseline → uniform cells.
 */

export interface SpriteFrame {
  index: number;
  row: number;
  col: number;
  direction: string;
  pose: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface SpriteAtlas {
  image: string;
  frameWidth: number;
  frameHeight: number;
  cols: number;
  rows: number;
  directions: string[];
  poses: string[];
  walkCycle: number[];
  idleFrame: number;
  frames: SpriteFrame[];
}

export interface ProcessedSprite {
  sheetDataUrl: string;
  atlas: SpriteAtlas;
}

export interface ProcessOptions {
  cols: number;
  rows: number;
  directions: string[];
  poses: string[];
  walkCycle: number[];
  idleFrame: number;
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Could not load the generated sheet'));
    img.src = src;
  });
}

/** most-frequent quantised colour over a subsample = the flat background */
function modeColor(d: Uint8ClampedArray): [number, number, number] {
  const hist = new Map<number, number>();
  for (let p = 0; p < d.length; p += 4 * 7) {
    const k = ((d[p] >> 3) << 10) | ((d[p + 1] >> 3) << 5) | (d[p + 2] >> 3);
    hist.set(k, (hist.get(k) ?? 0) + 1);
  }
  let bk = 0;
  let bn = -1;
  for (const [k, n] of hist) if (n > bn) { bn = n; bk = k; }
  return [((bk >> 10) & 31) << 3, ((bk >> 5) & 31) << 3, (bk & 31) << 3];
}

export async function processSpriteSheet(rawDataUrl: string, opts: ProcessOptions): Promise<ProcessedSprite> {
  const { cols, rows } = opts;
  const img = await loadImage(rawDataUrl);
  const iw = img.naturalWidth;
  const ih = img.naturalHeight;
  const oc = document.createElement('canvas');
  oc.width = iw;
  oc.height = ih;
  const ox = oc.getContext('2d', { willReadFrequently: true })!;
  ox.drawImage(img, 0, 0);
  const id = ox.getImageData(0, 0, iw, ih);
  const d = id.data;

  // 1. key background = mode colour, global within tolerance
  const [br, bg, bb] = modeColor(d);
  for (let i = 0; i < d.length; i += 4) {
    if (Math.abs(d[i] - br) + Math.abs(d[i + 1] - bg) + Math.abs(d[i + 2] - bb) < 105) d[i + 3] = 0;
  }
  ox.putImageData(id, 0, 0);

  const cw = iw / cols;
  const ch = ih / rows;
  const inset = Math.round(cw * 0.05);

  interface Crop { sx: number; sy: number; w: number; h: number; mask: Uint8Array; mw: number; mox: number; moy: number }
  const crops: Crop[] = [];

  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const x0 = Math.round(c * cw) + inset;
      const y0 = Math.round(r * ch) + inset;
      const x1 = Math.round((c + 1) * cw) - inset;
      const y1 = Math.round((r + 1) * ch) - inset;
      const W = x1 - x0;
      const H = y1 - y0;
      // label connected alpha components, keeping their sizes
      const lab = new Int32Array(W * H).fill(-1);
      const sizes = new Map<number, number>();
      const stack: number[] = [];
      for (let s = 0; s < W * H; s++) {
        if (lab[s] !== -1) continue;
        const sy0 = (s / W) | 0;
        const sx0 = s % W;
        if (d[((y0 + sy0) * iw + (x0 + sx0)) * 4 + 3] <= 40) { lab[s] = -2; continue; }
        lab[s] = s;
        stack.length = 0;
        stack.push(s);
        let n = 0;
        while (stack.length) {
          const pp = stack.pop()!;
          n++;
          const px = pp % W;
          const py = (pp / W) | 0;
          const nb = [pp - 1, pp + 1, pp - W, pp + W];
          const ok = [px > 0, px < W - 1, py > 0, py < H - 1];
          for (let k = 0; k < 4; k++) {
            if (!ok[k]) continue;
            const ni = nb[k];
            if (lab[ni] !== -1) continue;
            const ny = (ni / W) | 0;
            const nx = ni % W;
            if (d[((y0 + ny) * iw + (x0 + nx)) * 4 + 3] <= 40) { lab[ni] = -2; continue; }
            lab[ni] = s;
            stack.push(ni);
          }
        }
        sizes.set(s, n);
      }
      // KEEP every sizable component (character parts that keying can split — e.g. hair vs body — must
      // all survive), drop only tiny specks / shadow nubs relative to the biggest blob.
      let largest = 0;
      for (const n of sizes.values()) if (n > largest) largest = n;
      const thresh = Math.max(120, largest * 0.08);
      const keep = new Set<number>();
      for (const [root, n] of sizes) if (n >= thresh) keep.add(root);
      let mnX = W;
      let mnY = H;
      let mxX = 0;
      let mxY = 0;
      for (let ly = 0; ly < H; ly++) for (let lx = 0; lx < W; lx++) {
        if (keep.has(lab[ly * W + lx])) { if (lx < mnX) mnX = lx; if (lx > mxX) mxX = lx; if (ly < mnY) mnY = ly; if (ly > mxY) mxY = ly; }
      }
      if (mxX < mnX) { mnX = 0; mnY = 0; mxX = W - 1; mxY = H - 1; }
      const mask = new Uint8Array(W * H);
      for (let s = 0; s < W * H; s++) mask[s] = keep.has(lab[s]) ? 1 : 0;
      crops.push({ sx: x0 + mnX, sy: y0 + mnY, w: mxX - mnX + 1, h: mxY - mnY + 1, mask, mw: W, mox: mnX, moy: mnY });
    }
  }

  // 4. normalise to one common height (median), foot-aligned, uniform cells
  const heights = crops.map(c => c.h).slice().sort((a, b) => a - b);
  const targetH = heights[Math.floor(heights.length / 2)] || 1;
  const maxScaledW = Math.max(...crops.map(c => (c.w * targetH) / c.h));
  const frameH = Math.round(targetH * 1.16);
  const frameW = Math.round(Math.max(maxScaledW, targetH * 0.6) + targetH * 0.12);
  const pad = Math.round(targetH * 0.07);

  const out = document.createElement('canvas');
  out.width = frameW * cols;
  out.height = frameH * rows;
  const g = out.getContext('2d')!;
  g.imageSmoothingQuality = 'high';

  const frames: SpriteFrame[] = [];
  crops.forEach((cr, i) => {
    const r = Math.floor(i / cols);
    const c = i % cols;
    // masked crop → its own canvas (only the largest component)
    const tmp = document.createElement('canvas');
    tmp.width = cr.w;
    tmp.height = cr.h;
    const tx = tmp.getContext('2d')!;
    const im = tx.createImageData(cr.w, cr.h);
    const td = im.data;
    for (let ly = 0; ly < cr.h; ly++) for (let lx = 0; lx < cr.w; lx++) {
      const gx = cr.mox + lx;
      const gy = cr.moy + ly;
      if (cr.mask[gy * cr.mw + gx]) {
        const si = ((cr.sy + ly) * iw + (cr.sx + lx)) * 4;
        const di = (ly * cr.w + lx) * 4;
        td[di] = d[si]; td[di + 1] = d[si + 1]; td[di + 2] = d[si + 2]; td[di + 3] = d[si + 3];
      }
    }
    tx.putImageData(im, 0, 0);
    const s = targetH / cr.h;
    const dw = cr.w * s;
    const dh = cr.h * s;
    const dx = c * frameW + (frameW - dw) / 2;
    const dy = r * frameH + frameH - pad - dh; // foot baseline aligned
    g.drawImage(tmp, dx, dy, dw, dh);
    frames.push({
      index: i,
      row: r,
      col: c,
      direction: opts.directions[r] ?? String(r),
      pose: opts.poses[c] ?? String(c),
      x: c * frameW,
      y: r * frameH,
      w: frameW,
      h: frameH,
    });
  });

  return {
    sheetDataUrl: out.toDataURL('image/png'),
    atlas: {
      image: '',
      frameWidth: frameW,
      frameHeight: frameH,
      cols,
      rows,
      directions: opts.directions,
      poses: opts.poses,
      walkCycle: opts.walkCycle,
      idleFrame: opts.idleFrame,
      frames,
    },
  };
}
